/**
 * Los mensajes del taller, escritos una sola vez.
 *
 * El canal del negocio es WhatsApp: el cliente deja el equipo y después todo
 * pasa por ahí. Son tres momentos y siempre los mismos:
 *
 *   1. el presupuesto, apenas contesta el técnico;
 *   2. el aviso de que está listo;
 *   3. el saldo, para coordinar cómo lo termina de pagar.
 *
 * Escritos acá y no en cada botón porque improvisarlos cada vez es lo que hace
 * que uno diga "seña" y el otro "anticipo", que el cliente no entienda si ya
 * pagó algo, y que el precio aparezca en una moneda distinta según quién
 * escriba.
 *
 * REGLA DE PLATA
 * --------------
 * Los importes van SIEMPRE en la moneda del presupuesto, con su símbolo, y
 * nunca convertidos "a mano" en el texto. Un mensaje que dice un número en una
 * moneda que no se declara es la forma más barata de terminar cobrando mal.
 */
import { formatear } from "@tora/shared";

import { leerPlata, nombreOrden, type OrdenTaller } from "./logica";

/** El saludo. Sin nombre cuando la orden todavía no tiene cliente asociado. */
function hola(orden: OrdenTaller): string {
  const nombre = orden.cliente?.trim().split(/\s+/)[0];
  return nombre ? `Hola ${nombre}!` : "Hola!";
}

const FIRMA = "MANZANA MORDIDA";

/**
 * El presupuesto. Lo que sale, cuánto ya puso, y qué falta para arrancar.
 *
 * `anticipoPct` es el porcentaje que el local pide para empezar, y el número
 * que se le pide es lo que falta para llegar a ese porcentaje CONTANDO lo ya
 * pagado: el diagnóstico es plata del cliente que ya está en este trabajo.
 */
export function mensajePresupuesto(orden: OrdenTaller, anticipoPct: number): string {
  const { precio, senas, moneda } = leerPlata(orden);
  const total = precio ?? 0;
  const objetivo = Math.round(((total * anticipoPct) / 100) * 100) / 100;
  const falta = Math.max(Math.round((objetivo - senas) * 100) / 100, 0);

  const lineas = [hola(orden), "", `Ya revisamos tu ${orden.equipo} (${nombreOrden(orden)}).`];
  if (orden.diagnostico?.trim()) lineas.push(`Lo que encontramos: ${orden.diagnostico.trim()}`);
  lineas.push("", `La reparación sale ${formatear(total, moneda)}.`);

  if (senas > 0) {
    lineas.push(`De eso ya tenés ${formatear(senas, moneda)} pagos del diagnóstico.`);
  }
  if (falta > 0) {
    lineas.push(
      `Para arrancar necesitamos ${formatear(falta, moneda)} más (el ${anticipoPct}% del total).`,
    );
  } else if (total > 0) {
    lineas.push("Con lo que ya pagaste alcanza para arrancar.");
  }

  lineas.push("", "¿Lo hacemos?", FIRMA);
  return lineas.join("\n");
}

/** El equipo está listo. Todavía no habla de plata: eso es el mensaje del saldo. */
export function mensajeListo(orden: OrdenTaller): string {
  return [
    hola(orden),
    "",
    `Tu ${orden.equipo} ya está listo (${nombreOrden(orden)}).`,
    "Lo podés pasar a buscar cuando quieras, en el horario del local.",
    "",
    FIRMA,
  ].join("\n");
}

/**
 * El saldo, para coordinar cómo lo termina de pagar.
 *
 * Dice la regla sin rodeos —el equipo se entrega pago— porque decirla recién en
 * el mostrador, con el cliente esperando el teléfono en la mano, es donde se
 * terminan haciendo las excepciones.
 */
export function mensajeSaldo(orden: OrdenTaller): string {
  const { precio, senas, moneda } = leerPlata(orden);
  const total = precio ?? 0;
  const falta = Math.max(Math.round((total - senas) * 100) / 100, 0);

  const lineas = [hola(orden), "", `Tu ${orden.equipo} ya está listo (${nombreOrden(orden)}).`, ""];
  lineas.push(`Total de la reparación: ${formatear(total, moneda)}`);
  if (senas > 0) lineas.push(`Ya pagaste: ${formatear(senas, moneda)}`);
  lineas.push(`Queda: ${formatear(falta, moneda)}`);
  lineas.push(
    "",
    "¿Cómo lo abonás, efectivo o transferencia? El equipo se entrega con el total abonado.",
    FIRMA,
  );
  return lineas.join("\n");
}

/**
 * El talón de recepción, para mandarlo por WhatsApp en vez de imprimirlo.
 *
 * Un link de chat no puede adjuntar un PDF, así que el mensaje lleva el link a
 * la página del talón (`/t/:token`), que dice lo mismo que el papel con las
 * condiciones completas y se guarda como PDF desde el celular. Lo que el link
 * no reemplaza es la firma: por eso pide confirmar que lo recibió.
 */
export function mensajeTalon(orden: OrdenTaller, url: string): string {
  return [
    hola(orden),
    "",
    `Te dejamos la constancia de recepción de tu ${orden.equipo} (${nombreOrden(orden)}), con las condiciones del servicio:`,
    url,
    "",
    "¿Nos confirmás que la recibiste?",
    FIRMA,
  ].join("\n");
}
